import { EXIT_DATE_ALIAS } from 'common/constants/allocations';
import { largeCurrencyFormat, smallCurrencyFormat } from 'common/formats/formats';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import CaptableList from 'pages/Waterfall/components/CaptableList/CaptableList';
import CaptableListValueViewer from 'pages/Waterfall/components/CaptableList/CaptableListValueViewer';
import CompanyList from 'pages/Waterfall/components/CompanyList/CompanyList';
import CompanyListValueViewer from 'pages/Waterfall/components/CompanyList/CompanyListValueViewer';
import MeasurementDateList from 'pages/Waterfall/components/MeasurementDateList/MeasurementDateList';
import MeasurementDateListValueViewer from 'pages/Waterfall/components/MeasurementDateList/MeasurementDateValueViewer';
import {
  CAP_TABLE_ALIAS,
  COMPANY_ALIAS,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
  FOREIGN_EXCHANGE_RATE_BASE_ALIAS,
  LESS_DEBT_ALIAS,
  MEASUREMENT_DATE_ALIAS,
  PLUS_CASH_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import { INPUT_SHEET_HEADER_ALIAS } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/constants';
import { StandardExchangeRateRowLabel } from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/WaterfallFXRowLabel/WaterfallFXRowLabel';
import { PRISM_SEMANTIC_COLORS } from 'theme';
import palette from 'theme/palette';
import { InputSheetRow, InputSheetRowConfigProps } from './inputSheetRowConfig.props';
import {
  GridDate,
  GridDateEditor,
  GridDateValueViewer,
} from '../../../../../components/FeaturedSpreadsheet/components';

// Keep in sync with InputSheetParser.
const CURRENCY_SELECTOR_ALIAS = 'fund_currency_selector';

const inputSheetRowConfig = ({
  exitDate,
  capTableId,
  equityValue,
  enterpriseValue,
  plusCash,
  lessDebt,
  firmOptions,
  childrenCapTables,
  exitProceedsExpression,
  plusCashIsFromFinancials,
  lessDebtIsFromFinancials,
  isAdditionalCurrencySelected,
  exchangeRate,
  isExchangeRateOverridden,
  exchangeRateInputCurrency,
  inputScale,
}: InputSheetRowConfigProps): InputSheetRow[] => {
  const financialInputFormat = { ...largeCurrencyFormat, inputScale };
  const regularCurrencyFormat = {
    ...smallCurrencyFormat,
    inputScale: 1,
  };
  const regularCurrencyCustomFormat = { currency: { units: REGULAR_UNIT } };
  const defaultValueColor = PRISM_SEMANTIC_COLORS.feedback.success;
  const overriddenValueColor = palette.primary[700];
  const resolvedExchangeRate =
    exchangeRate != null && Number.isFinite(exchangeRate) && exchangeRate > 0 ? exchangeRate : null;
  const isExchangeRateReady = resolvedExchangeRate != null;
  const exchangeRateStyle = {
    color: isExchangeRateOverridden ? overriddenValueColor : defaultValueColor,
  };
  const cashStyle = {
    color: plusCashIsFromFinancials ? defaultValueColor : overriddenValueColor,
  };
  const debtStyle = {
    color: lessDebtIsFromFinancials ? defaultValueColor : overriddenValueColor,
  };
  let baseExchangeRateValue: number | null = null;
  let foreignExchangeRateValue: number | null = null;
  if (resolvedExchangeRate != null) {
    baseExchangeRateValue = 1;
    foreignExchangeRateValue = resolvedExchangeRate;
    if (isExchangeRateOverridden && exchangeRateInputCurrency === 'base') {
      baseExchangeRateValue = 1 / resolvedExchangeRate;
      foreignExchangeRateValue = 1;
    }
  }

  const currencyRow = {
    name: 'Currency',
    alias: CURRENCY_SELECTOR_ALIAS,
    titleAlias: 'fund_currency_header',
    className: 'waterfall-currency-row',
    value: undefined,
    tooltipMessages: [],
    valueViewer: StandardExchangeRateRowLabel,
  };
  const headerRow: InputSheetRow = {
    name: '',
    alias: INPUT_SHEET_HEADER_ALIAS,
    titleAlias: `${INPUT_SHEET_HEADER_ALIAS}_label`,
    className: 'division-bottom-only',
    value: '',
    foreignValue: '',
    tooltipMessages: [],
    readOnly: true,
  };
  const rows: InputSheetRow[] = [
    {
      name: 'Cap Table',
      alias: CAP_TABLE_ALIAS,
      titleAlias: 'cap_table_header',
      tooltipMessages: ['Default is the primary cap table. Select a pro forma or alternate version if available.'],
      value: capTableId || undefined,
      dataEditor: (props: any) => <CaptableList {...props} childrenCapTables={childrenCapTables} />,
      valueViewer: (props: any) => <CaptableListValueViewer {...props} childrenCapTables={childrenCapTables} />,
    },
    {
      name: 'Exit Date',
      alias: EXIT_DATE_ALIAS,
      titleAlias: 'exit_date_header',
      value: exitDate || '',
      tooltipMessages: ['The date through which dividends and any accrued return are calculated.'],
      valueViewer: (props: any) => <GridDateValueViewer {...props} />,
      dataEditor: (props: any) => <GridDateEditor {...props} />,
      gridDateComponent: GridDate as InputSheetRow['gridDateComponent'],
    },
    {
      name: 'Foreign Exchange Rate',
      alias: FOREIGN_EXCHANGE_RATE_BASE_ALIAS,
      titleAlias: 'foreign_exchange_rate_header',
      value: baseExchangeRateValue,
      foreignValue: foreignExchangeRateValue,
      tooltipMessages: [
        'The conversion rate from the Cap Table currency to the selected foreign currency as of the Exit Date.',
      ],
      format: regularCurrencyFormat,
      customFormat: regularCurrencyCustomFormat,
      readOnly: !isAdditionalCurrencySelected,
      style: exchangeRateStyle,
    },
    {
      name: 'Exit Enterprise Value',
      alias: EXIT_ENTERPRISE_VALUE_ALIAS,
      titleAlias: `${EXIT_ENTERPRISE_VALUE_ALIAS}_header`,
      value: enterpriseValue ?? 0,
      tooltipMessages: ['Enter either Enterprise Value or Equity Value; the other is calculated using cash and debt.'],
      format: financialInputFormat,
      customKey: EXIT_ENTERPRISE_VALUE_ALIAS,
      foreignValue: isExchangeRateReady ? undefined : null,
    },
    {
      name: 'Plus Cash',
      alias: PLUS_CASH_ALIAS,
      titleAlias: `${PLUS_CASH_ALIAS}_header`,
      value: plusCash,
      tooltipMessages: [
        'Pulled from the Balance Sheet by default (displayed green). Override with custom inputs if desired (displayed blue).',
      ],
      format: financialInputFormat,
      readOnly: false,
      titleClassName: 'subtitle-indented',
      style: cashStyle,
      foreignValue: isExchangeRateReady ? undefined : null,
    },
    {
      name: 'Less Debt',
      alias: LESS_DEBT_ALIAS,
      titleAlias: `${LESS_DEBT_ALIAS}_header`,
      value: lessDebt,
      tooltipMessages: [
        'Pulled from the Balance Sheet by default (displayed green). Override with custom inputs if desired (displayed blue).',
      ],
      format: financialInputFormat,
      readOnly: false,
      titleClassName: 'subtitle-indented',
      style: debtStyle,
      foreignValue: isExchangeRateReady ? undefined : null,
    },
    {
      name: 'Exit Equity Value',
      alias: EXIT_EQUITY_ALIAS,
      titleAlias: 'exit_equity_value_header',
      value: equityValue ?? 0,
      tooltipMessages: ['Enter either Enterprise Value or Equity Value; the other is calculated using cash and debt.'],
      format: financialInputFormat,
      customKey: EXIT_EQUITY_ALIAS,
      foreignValue: isExchangeRateReady ? undefined : null,
    },
    {
      name: 'Firm Total Exit Proceeds',
      alias: FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
      value: undefined,
      titleAlias: `${FIRM_TOTAL_EXIT_PROCEEDS_ALIAS}_header`,
      format: financialInputFormat,
      expr: exitProceedsExpression,
      className: 'bold-row',
    },
  ];

  if (!firmOptions) {
    return [headerRow, currencyRow, ...rows];
  }

  const firmOptionRows: InputSheetRow[] = [
    {
      name: 'Company',
      alias: COMPANY_ALIAS,
      titleAlias: 'company_header',
      value: firmOptions.companyId,
      valueViewer: (props: any) => <CompanyListValueViewer {...props} />,
      dataEditor: (props: any) => <CompanyList {...props} />,
    },
    {
      name: 'Cap Table Date',
      alias: MEASUREMENT_DATE_ALIAS,
      tooltipMessages: ['The date of the analysis, reflecting the company’s information as of that time.'],
      titleAlias: 'measurement_date_header',
      value: firmOptions.measurementDateId,
      valueViewer: (props: any) => <MeasurementDateListValueViewer {...props} />,
      dataEditor: (props: any) => <MeasurementDateList {...props} />,
    },
  ];

  return [headerRow, currencyRow, ...firmOptionRows, ...rows];
};

export default inputSheetRowConfig;
